import { Theme, css } from '@emotion/react';
import { FC, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { IMaskInput } from 'react-imask';

import {
  addPostalCodePrefix,
  getPostalCodeNormalize,
} from '~src/address/search-address/postal-code-field';
import { useOutsideClick } from '~src/commons/utils/hooks';
import { useFocus, useOutSideIframeClick } from '~src/commons/utils/hooks/checkout-hooks';
import { useFormFieldScroll } from '~src/commons/utils/hooks/use-form-field-scroll';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { useGlobalState } from '~src/global-state';
import { fontStyles } from '~src/theme/fontStyles';
import { mediaQueries } from '~src/theme/mediaQueries';
import { getBorderColor, getLabelColor } from '~src/ui/common-styles';
import { InputError } from '~src/ui/input-error';
import * as inputStyles from '~src/ui/text-input.style';

import {
  getPostalCodeFieldConfig,
  getPostalCodeValidationRegexByCountryCode,
} from '~address-form/validation/postal-code-validation';

import { AddressFormFields } from '../types';
import { getValidationValue } from '../utils/hook-form-utils';
import { StandardInputProps } from './standard-input';

interface PostalCodeInputProps extends StandardInputProps {}

export const PostalCodeInput: FC<PostalCodeInputProps> = ({
  name,
  warning,
  label,
  dataTest,
  validationRules,
  ...props
}) => {
  const [focused, setFocused] = useState(false);
  const globalState = useGlobalState();
  const country = globalState.purchase_country;
  const postalCodeConfig = getPostalCodeFieldConfig(country);
  const { focusProps, isFocused } = useFocus();

  const { tICU } = useTranslations();

  const normalizePostalCode = getPostalCodeNormalize(country);

  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext<AddressFormFields>();

  const value = getValues(name);
  const isLabelRaised = isFocused || !!value;
  const hasError = !!errors[name];

  const inputRef = useRef<HTMLInputElement | null>(null);
  useFormFieldScroll(name, inputRef);
  const mouseDown = useRef(false);

  const handleFocus = () => {
    if (!focusProps) {
      return;
    }
    focusProps.onFocus();
  };

  const handleBlur = () => {
    if (!focusProps || mouseDown.current) {
      handleFocus();
      mouseDown.current = false;
    } else {
      focusProps.onBlur();
    }
  };

  const wrapperRef = useOutsideClick(() => {
    handleBlur();
  }, [isFocused]);

  useOutSideIframeClick(() => {
    handleBlur();
  }, [isFocused]);

  return (
    <>
      <div
        css={styles.inputWrapper(focused, hasError, warning)}
        ref={wrapperRef}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      >
        <div css={inputStyles.inputAndLabelWrapper}>
          <label
            htmlFor={name}
            css={styles.label(isLabelRaised, hasError, warning)}
            aria-hidden={hasError ? 'false' : 'true'}
          >
            {label}
          </label>

          <Controller
            rules={{
              pattern: {
                value: getPostalCodeValidationRegexByCountryCode(country),
                message: tICU('widget.ADDRESS_MODAL.POSTAL_CODE_VALIDATION'),
              },
              ...validationRules,
            }}
            name={name}
            control={control}
            render={({ field: { onBlur, value, ref } }) => (
              <IMaskInput
                {...props}
                {...postalCodeConfig}
                css={styles.input()}
                inputRef={e => {
                  if (!!inputRef) {
                    inputRef.current = e;
                  }

                  ref(e);
                }}
                value={addPostalCodePrefix(value as string, country)}
                onBlur={() => {
                  onBlur();
                  focusProps.onBlur();
                }}
                onFocus={() => {
                  focusProps.onFocus();
                }}
                onAccept={value => setValue(name, normalizePostalCode(value)?.toString())}
                data-test={dataTest}
                aria-invalid={hasError}
                aria-required={!!getValidationValue(validationRules?.required)}
              />
            )}
          />
        </div>
      </div>
      {hasError && <InputError dataTest={`${name}-error`}>{errors[name]?.message}</InputError>}
    </>
  );
};

export const styles = {
  wrapper: css({
    position: 'relative',
    height: '60px',
    width: '100%',
    ...fontStyles.px14_20,
  }),
  inputWrapper: (focused: boolean, error: boolean, warning?: boolean) => (theme: Theme) =>
    css({
      backgroundColor: 'transparent',
      borderColor: getBorderColor(focused, error, warning)(theme),
      borderStyle: 'solid',
      borderWidth: '1px',
      borderRadius: '2px',
      transition: 'all 0.2s',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    }),
  input: (isExtensionElementVisible?: boolean) => (theme: Theme) =>
    css({
      border: 'none',
      color: theme.black,
      height: '100%',
      width: '100%',
      outline: 'none',
      WebkitAppearance: 'none',
      boxSizing: 'border-box',
      padding: isExtensionElementVisible ? '29px 16px 12px 4px' : '29px 16px 12px 16px',
      ...fontStyles.px14_20,
      resize: 'none',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      [mediaQueries.S]: {
        ...fontStyles.px16_20, // disable auto-zoom on ios.
      },
    }),
  label: (raiseLabel: boolean, error?: boolean, warning?: boolean) => (theme: Theme) =>
    css({
      position: 'absolute',
      display: 'flex',
      justifyContent: 'space-between',
      top: raiseLabel ? '11px' : '50%',
      transform: `translate(0, ${raiseLabel ? '0' : '-50%'})`,
      left: '16px',
      right: '16px',
      overflow: 'hidden',
      color: getLabelColor(!!error, warning)(theme),
      transition: 'all .1s ease-in-out',
      pointerEvents: 'none',
      cursor: 'text',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      ...(raiseLabel ? fontStyles.px12_16 : fontStyles.px16_20),
    }),
};
